import type {
  GroupProDto,
  ReceiptScanSuggestionDto,
  ScanErrorDetails,
  ScanQuotaDto,
  UserProDto,
} from '@splitfiesta/shared';
import {
  PRO_ERROR_KEYS,
  SCAN_ERROR_KEYS,
  toMinorUnits,
} from '@splitfiesta/shared';

import { groupPro, userPro } from '../pro/pro';
import { translationKeyOf } from '../utils/apiErrors';

export type ScanAvailability =
  | { kind: 'hidden' }
  | { kind: 'offline' }
  | { kind: 'notCounted' }
  | { kind: 'counted'; left: number; allowance: number }
  /** `fairUse`: the server's fair-use cap, which Pro hits too; no upsell applies. */
  | { kind: 'exhausted'; fairUse?: true }
  | { kind: 'unknown' };

export function scanAvailability(input: {
  flagOn: boolean;
  proNotAvailableSeen: boolean;
  fairUseHit: boolean;
  isOnline: boolean;
  isLocalRow: boolean;
  isExampleGroup: boolean;
  user:
    { pro?: UserProDto; scanQuota?: ScanQuotaDto | null } | null | undefined;
  group: { pro?: GroupProDto } | null | undefined;
  nowMonth: string;
}): ScanAvailability {
  if (
    !input.flagOn ||
    input.proNotAvailableSeen ||
    input.isExampleGroup ||
    input.isLocalRow
  ) {
    return { kind: 'hidden' };
  }
  if (!input.isOnline) return { kind: 'offline' };
  // Fair use caps Pro callers too; the server's refusal is the only source.
  if (input.fairUseHit) return { kind: 'exhausted', fairUse: true };
  if (userPro(input.user).active || groupPro(input.group).active) {
    return { kind: 'notCounted' };
  }

  const quota = input.user?.scanQuota;
  if (quota === null) return { kind: 'notCounted' };
  if (quota === undefined) return { kind: 'unknown' };
  // The server resets the row on the first scan of a month, so an older
  // stored month already means a full allowance.
  if (quota.month < input.nowMonth) {
    return {
      kind: 'counted',
      left: quota.allowance,
      allowance: quota.allowance,
    };
  }
  if (quota.used >= quota.allowance || quota.attempts >= quota.attemptCap) {
    return { kind: 'exhausted' };
  }
  return {
    kind: 'counted',
    left: quota.allowance - quota.used,
    allowance: quota.allowance,
  };
}

export type ScanUsage =
  | { kind: 'hidden' }
  | { kind: 'pro' }
  | { kind: 'counted'; used: number; allowance: number };

export function scanUsage(input: {
  flagOn: boolean;
  user:
    { pro?: UserProDto; scanQuota?: ScanQuotaDto | null } | null | undefined;
  nowMonth: string;
}): ScanUsage {
  if (!input.flagOn) return { kind: 'hidden' };
  if (userPro(input.user).active) return { kind: 'pro' };
  const quota = input.user?.scanQuota;
  if (quota === null) return { kind: 'pro' };
  if (quota === undefined) return { kind: 'hidden' };
  // The server resets the row on the first scan of a month, so an older
  // stored month has nothing used yet.
  const used = quota.month < input.nowMonth ? 0 : quota.used;
  return { kind: 'counted', used, allowance: quota.allowance };
}

/** `YYYY-MM` in UTC: the quota month is UTC on the server. */
export function currentUtcMonth(now: Date = new Date()): string {
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

export type ScanFieldKey = 'amount' | 'title' | 'date' | 'category';

/** The suggestion fields `mergeScan` reads; a voice draft maps onto them too. */
export type ScanFieldSuggestion = Pick<
  ReceiptScanSuggestionDto,
  | 'amount'
  | 'currency'
  | 'currencyPrinted'
  | 'currencyEvidence'
  | 'merchant'
  | 'titleSuggestion'
  | 'date'
  | 'category'
  | 'subcategory'
>;

export interface ScanFormSnapshot {
  amount: number | null;
  currency: string;
  title: string;
  date: string | null;
  category: string | null;
  subcategory: string | null;
  touched: {
    amount: boolean;
    title: boolean;
    date: boolean;
    categoryManual: boolean;
  };
  aiTagged: Set<ScanFieldKey>;
}

export interface ScanMerge {
  fills: Partial<{
    amount: number;
    currency: string;
    title: string;
    date: string;
    category: string;
    subcategory: string | null;
  }>;
  useRows: ScanUseRow[];
  tags: ScanFieldKey[];
  checkCurrency: boolean;
}

export type ScanUseSide = 'mine' | 'theirs';

export interface ScanUseCategory {
  category: string;
  subcategory: string | null;
}

export interface ScanUseRow {
  field: ScanFieldKey;
  /** A category row's sides are `category/subcategory` keys; `mineCategory`/`theirsCategory` carry them split. */
  mine: string | number;
  theirs: string | number;
  /** Amount rows: the currency each side is in. They differ whenever the row is a currency change. */
  mineCurrency?: string;
  theirsCurrency?: string;
  mineCategory?: ScanUseCategory;
  theirsCategory?: ScanUseCategory;
  apply: Partial<ScanMerge['fills']>;
}

/**
 * A field counts as empty when untouched, or when it still carries its AI
 * tag; on the edit form every hydrated value is touched. A touched field
 * with no value is empty too: a Use row against a blank would ask the person
 * to keep nothing.
 */
export function mergeScan(
  form: ScanFormSnapshot,
  s: ScanFieldSuggestion,
  groupCurrency: string
): ScanMerge {
  const merge: ScanMerge = {
    fills: {},
    useRows: [],
    tags: [],
    checkCurrency: false,
  };
  // An AI-tagged value is still the previous scan's, not the person's, so a
  // new read may overwrite it.
  const isEmpty = (field: ScanFieldKey, touched: boolean, hasValue: boolean) =>
    !touched || form.aiTagged.has(field) || !hasValue;

  if (s.amount !== null) {
    const unresolved = s.currency === null && s.currencyPrinted !== null;
    merge.checkCurrency = s.currencyEvidence === 'group_hint' || unresolved;
    const formCurrency = form.currency || groupCurrency;
    const receiptCurrency =
      merge.checkCurrency || s.currency === null ? null : s.currency;
    const currencyChange =
      receiptCurrency !== null && receiptCurrency !== formCurrency
        ? { currency: receiptCurrency }
        : {};

    if (isEmpty('amount', form.touched.amount, !!form.amount)) {
      Object.assign(merge.fills, currencyChange, { amount: s.amount });
      merge.tags.push('amount');
    } else if (
      'currency' in currencyChange ||
      toMinorUnits(form.amount!, formCurrency) !==
        toMinorUnits(s.amount, formCurrency)
    ) {
      merge.useRows.push({
        field: 'amount',
        mine: form.amount!,
        theirs: s.amount,
        mineCurrency: formCurrency,
        theirsCurrency: currencyChange.currency ?? formCurrency,
        apply: { ...currencyChange, amount: s.amount },
      });
    }
  }

  const title = s.merchant?.trim() || s.titleSuggestion?.trim() || null;
  if (title !== null) {
    if (isEmpty('title', form.touched.title, form.title.trim() !== '')) {
      merge.fills.title = title;
      merge.tags.push('title');
    } else if (form.title.trim() !== title) {
      merge.useRows.push({
        field: 'title',
        mine: form.title,
        theirs: title,
        apply: { title },
      });
    }
  }

  if (s.date !== null) {
    if (isEmpty('date', form.touched.date, !!form.date)) {
      merge.fills.date = s.date;
      merge.tags.push('date');
    } else if (form.date !== s.date) {
      merge.useRows.push({
        field: 'date',
        mine: form.date!,
        theirs: s.date,
        apply: { date: s.date },
      });
    }
  }

  if (s.category !== null) {
    const apply = { category: s.category, subcategory: s.subcategory };
    if (isEmpty('category', form.touched.categoryManual, !!form.category)) {
      Object.assign(merge.fills, apply);
      merge.tags.push('category');
    } else if (
      form.category !== s.category ||
      (form.subcategory ?? null) !== (s.subcategory ?? null)
    ) {
      merge.useRows.push({
        field: 'category',
        mine: categoryKey(form.category!, form.subcategory),
        theirs: categoryKey(s.category, s.subcategory),
        mineCategory: {
          category: form.category!,
          subcategory: form.subcategory ?? null,
        },
        theirsCategory: { category: s.category, subcategory: s.subcategory },
        apply,
      });
    }
  }

  return merge;
}

/** `category/subcategory`, so a row raised by a subcategory alone still shows the difference. */
function categoryKey(category: string, subcategory: string | null): string {
  return subcategory ? `${category}/${subcategory}` : category;
}

export interface DescribeUseRowContext {
  formatCurrency: (amount: number, currency: string) => string;
  /** `YYYY-MM-DD` to the locale's date. */
  formatDate: (isoDate: string) => string;
  categoryLabel: (category: string) => string;
  subcategoryLabel: (category: string, subcategory: string) => string;
  fieldLabel: (field: ScanFieldKey) => string;
  /** Joins a category and its subcategory; a localized string, the same on web and mobile. */
  separator: string;
  /** Used when a row carries no currency of its own. */
  fallbackCurrency: string;
}

/** One side of a Use row in the person's words: money in that side's currency, a local date, category names. */
export function describeUseRow(
  row: ScanUseRow,
  side: ScanUseSide,
  ctx: DescribeUseRowContext
): { label: string; value: string } {
  const value = side === 'mine' ? row.mine : row.theirs;
  const label = ctx.fieldLabel(row.field);
  switch (row.field) {
    case 'amount': {
      const currency =
        (side === 'mine' ? row.mineCurrency : row.theirsCurrency) ??
        ctx.fallbackCurrency;
      return { label, value: ctx.formatCurrency(Number(value), currency) };
    }
    case 'date':
      return { label, value: ctx.formatDate(String(value)) };
    case 'category': {
      const parsed = side === 'mine' ? row.mineCategory : row.theirsCategory;
      const [category, subcategory = null] = parsed
        ? [parsed.category, parsed.subcategory]
        : String(value).split('/');
      const main = ctx.categoryLabel(category);
      return {
        label,
        value: subcategory
          ? `${main}${ctx.separator}${ctx.subcategoryLabel(category, subcategory)}`
          : main,
      };
    }
    default:
      return { label, value: String(value) };
  }
}

/**
 * The server saves without a draft it can no longer find, keeping whatever
 * receipt was there: no receipt, or the one from before the save, means the
 * new photo was dropped.
 */
export function receiptDraftDropped(args: {
  sentDraftId: string | null | undefined;
  previousReceipt?: string | null;
  savedReceipt: string | null | undefined;
}): boolean {
  if (!args.sentDraftId) return false;
  if (!args.savedReceipt) return true;
  return !!args.previousReceipt && args.savedReceipt === args.previousReceipt;
}

/** A Pro caller's fair-use refusal carries `quota: null`: it is not subject to the quota. */
export type ScanRefusalDetails = Omit<ScanErrorDetails, 'quota'> & {
  quota?: ScanQuotaDto | null;
};

export function isScanQuotaError(error: unknown): error is {
  translationKey: 'errors:scan.quotaExhausted';
  details?: ScanRefusalDetails | null;
} {
  return translationKeyOf(error) === SCAN_ERROR_KEYS.quotaExhausted;
}

/** The quota a refusal carries, and whether it is the fair-use cap (which a null quota can only be). */
export function scanRefusal(details: ScanRefusalDetails | null | undefined): {
  quota: ScanQuotaDto | null | undefined;
  fairUse: boolean;
} {
  const quota = details?.quota;
  return { quota, fairUse: details?.fairUse === true || quota === null };
}

export function isProNotAvailableError(error: unknown): boolean {
  return translationKeyOf(error) === PRO_ERROR_KEYS.notAvailable;
}

export function isDraftNotFoundError(error: unknown): boolean {
  return translationKeyOf(error) === SCAN_ERROR_KEYS.draftNotFound;
}

export function isPdfUnreadableError(error: unknown): boolean {
  return translationKeyOf(error) === SCAN_ERROR_KEYS.pdfUnreadable;
}
